import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDateForInput, getCurrentDateIST } from "@/lib/dateUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AddTrade = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Accounts and Strategies
  const [accounts, setAccounts] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [showStrategyDialog, setShowStrategyDialog] = useState(false);
  const [newAccountName, setNewAccountName] = useState("");
  const [newAccountBroker, setNewAccountBroker] = useState("");
  const [newStrategyName, setNewStrategyName] = useState("");
  const [newStrategyDescription, setNewStrategyDescription] = useState("");
  
  // Futures symbols from instrument master
  const [futuresSymbols, setFuturesSymbols] = useState([]);
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  
  const [formData, setFormData] = useState({
    account_id: "",
    strategy_id: "",
    symbol: "",
    exchange: "NSE",
    contract_expiry: "",
    direction: "LONG",
    entry_date: new Date().toISOString().split('T')[0],
    entry_price: "",
    num_lots: "1",
    lot_size: "",
    entry_comments: "",
  });

  const [contractExpiries, setContractExpiries] = useState([]);
  const [loadingExpiries, setLoadingExpiries] = useState(false);

  useEffect(() => {
    fetchAccounts();
    fetchStrategies();
    fetchFuturesSymbols(formData.exchange);
  }, []);

  // Fetch futures symbols when exchange changes
  useEffect(() => {
    fetchFuturesSymbols(formData.exchange);
  }, [formData.exchange]);

  const fetchFuturesSymbols = async (exchange) => {
    setLoadingSymbols(true);
    try {
      const response = await api.get("/futures-symbols", {
        params: { exchange }
      });
      
      if (response.data.status && response.data.symbols) {
        // Filter out test symbols and format for combobox
        const symbols = response.data.symbols
          .filter(s => !s.includes('TEST') && !s.includes('NSETEST'))
          .map(s => ({ value: s, label: s }));
        setFuturesSymbols(symbols);
      }
    } catch (error) {
      console.error("Error fetching futures symbols:", error);
      setFuturesSymbols([]);
    } finally {
      setLoadingSymbols(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await api.get("/accounts");
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  const fetchStrategies = async () => {
    try {
      const response = await api.get("/strategies");
      setStrategies(response.data);
    } catch (error) {
      console.error("Error fetching strategies:", error);
    }
  };

  // Fetch contract expiries when symbol changes
  const fetchContractExpiries = async (symbol, exchange) => {
    if (!symbol || symbol.length < 2) {
      setContractExpiries([]);
      return;
    }
    
    setLoadingExpiries(true);
    try {
      const response = await api.get("/contract-expiries", {
        params: { symbol, exchange }
      });
      
      if (response.data.status && response.data.data) {
        setContractExpiries(response.data.data);
        // Auto-select first (nearest) expiry if available
        if (response.data.data.length > 0 && !formData.contract_expiry) {
          setFormData(prev => ({ ...prev, contract_expiry: response.data.data[0].expiry }));
        }
      } else {
        setContractExpiries([]);
      }
    } catch (error) {
      console.error("Error fetching contract expiries:", error);
      setContractExpiries([]);
    } finally {
      setLoadingExpiries(false);
    }
  };

  // Effect to fetch expiries when symbol changes
  useEffect(() => {
    if (formData.symbol && formData.symbol.length >= 2) {
      // Extract base symbol (remove any existing expiry info)
      const baseSymbol = formData.symbol.replace(/\d{2}[A-Z]{3}\d{0,2}(FUT)?$/i, '').trim();
      if (baseSymbol) {
        fetchContractExpiries(baseSymbol, formData.exchange);
      }
    }
  }, [formData.symbol, formData.exchange]);

  // Auto-fill strategy based on previous trades for same symbol + account
  useEffect(() => {
    const fetchDefaultStrategy = async () => {
      if (formData.symbol && formData.account_id && !formData.strategy_id) {
        try {
          const response = await api.get(
            `/trades/default-strategy/${formData.account_id}/${encodeURIComponent(formData.symbol)}`
          );
          if (response.data.status && response.data.data?.strategy_id) {
            setFormData(prev => ({ ...prev, strategy_id: response.data.data.strategy_id }));
          }
        } catch (error) {
          // Silently ignore - no default strategy found
          console.debug("No default strategy found:", error);
        }
      }
    };
    fetchDefaultStrategy();
  }, [formData.symbol, formData.account_id]);

  // Auto-fill lot size when contract expiry is selected
  useEffect(() => {
    if (formData.contract_expiry && contractExpiries.length > 0) {
      const selectedContract = contractExpiries.find(c => c.expiry === formData.contract_expiry);
      if (selectedContract && selectedContract.lotsize && !formData.lot_size) {
        setFormData(prev => ({ ...prev, lot_size: selectedContract.lotsize.toString() }));
      }
    }
  }, [formData.contract_expiry, contractExpiries]);

  const handleAddAccount = async () => {
    if (!newAccountName.trim()) {
      toast.error("Please enter account name");
      return;
    }
    
    try {
      const response = await api.post("/accounts", {
        name: newAccountName,
        broker: newAccountBroker
      });
      
      setAccounts([...accounts, response.data]);
      setFormData({ ...formData, account_id: response.data.id });
      toast.success("Account added successfully");
      setShowAccountDialog(false);
      setNewAccountName("");
      setNewAccountBroker("");
    } catch (error) {
      console.error("Error adding account:", error);
      toast.error("Failed to add account");
    }
  };

  const handleAddStrategy = async () => {
    if (!newStrategyName.trim()) {
      toast.error("Please enter strategy name");
      return;
    }
    
    try {
      const response = await api.post("/strategies", {
        name: newStrategyName,
        description: newStrategyDescription
      });
      
      setStrategies([...strategies, response.data]);
      setFormData({ ...formData, strategy_id: response.data.id });
      toast.success("Strategy added successfully");
      setShowStrategyDialog(false);
      setNewStrategyName("");
      setNewStrategyDescription("");
    } catch (error) {
      console.error("Error adding strategy:", error);
      toast.error("Failed to add strategy");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contract_expiry) {
      toast.error("Please select a contract expiry");
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        ...formData,
        entry_price: parseFloat(formData.entry_price),
        num_lots: parseInt(formData.num_lots),
        lot_size: parseInt(formData.lot_size),
      };

      await api.post("/trades", payload);
      toast.success("Trade added successfully");
      navigate("/trades");
    } catch (error) {
      console.error("Error adding trade:", error);
      toast.error("Failed to add trade");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Direction-based styling (same as Edit Trade)
  const directionColor = formData.direction === "LONG" ? "border-profit/50" : "border-loss/50";
  const directionBg = formData.direction === "LONG" ? "bg-profit/5" : "bg-loss/5";
  const directionBadge = formData.direction === "LONG" 
    ? "bg-profit/20 text-profit border-profit/30" 
    : "bg-loss/20 text-loss border-loss/30";

  return (
    <div data-testid="add-trade-page">
      <button
        onClick={() => navigate("/trades")}
        className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Trades
      </button>

      <div className="mb-8 flex items-center gap-4">
        <div>
          <h1 className="font-heading font-semibold tracking-tight text-4xl sm:text-5xl text-white mb-2">
            Add New Trade
          </h1>
          <p className="text-slate-400 text-base">Record a new trade entry</p>
        </div>
        {/* Direction Badge */}
        <div className={`px-4 py-2 rounded-md border ${directionBadge} font-semibold text-lg`}>
          {formData.direction}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl">
        <div className={`bg-charcoal/50 border-2 ${directionColor} ${directionBg} rounded-md p-6 space-y-6 transition-colors`}>
          {/* Account and Strategy Selection - TOP PRIORITY */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4 border-b border-white/10">
            <div className="space-y-2">
              <Label htmlFor="account_id" className="text-slate-300 text-sm font-medium">
                Account <span className="text-red-400">*</span>
              </Label>
              <div className="flex gap-2">
                <Select
                  value={formData.account_id}
                  onValueChange={(value) => handleChange("account_id", value)}
                >
                  <SelectTrigger
                    id="account_id"
                    data-testid="account-select"
                    className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-white"
                  >
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name} {account.broker && `(${account.broker})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={() => setShowAccountDialog(true)}
                  className="bg-white/10 hover:bg-white/20 text-white px-3"
                  data-testid="add-account-button"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="strategy_id" className="text-slate-300 text-sm font-medium">
                Trading Strategy <span className="text-red-400">*</span>
              </Label>
              <div className="flex gap-2">
                <Select
                  value={formData.strategy_id}
                  onValueChange={(value) => handleChange("strategy_id", value)}
                >
                  <SelectTrigger
                    id="strategy_id"
                    data-testid="strategy-select"
                    className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-white"
                  >
                    <SelectValue placeholder="Select strategy" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    {strategies.map((strategy) => (
                      <SelectItem key={strategy.id} value={strategy.id}>
                        {strategy.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={() => setShowStrategyDialog(true)}
                  className="bg-white/10 hover:bg-white/20 text-white px-3"
                  data-testid="add-strategy-button"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Exchange and Symbol */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="exchange" className="text-slate-300 text-sm font-medium">
                Exchange
              </Label>
              <Select
                value={formData.exchange}
                onValueChange={(value) => {
                  handleChange("exchange", value);
                  handleChange("symbol", "");
                  handleChange("contract_expiry", "");
                  setContractExpiries([]);
                }}
              >
                <SelectTrigger
                  id="exchange"
                  data-testid="exchange-select"
                  className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-white"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border-white/10">
                  <SelectItem value="NSE" data-testid="exchange-nse">NSE</SelectItem>
                  <SelectItem value="MCX" data-testid="exchange-mcx">MCX</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="symbol" className="text-slate-300 text-sm font-medium">
                Symbol <span className="text-slate-500 text-xs">({futuresSymbols.length} available)</span>
              </Label>
              <Combobox
                options={futuresSymbols}
                value={formData.symbol}
                onChange={(value) => {
                  handleChange("symbol", value);
                  handleChange("contract_expiry", "");
                }}
                placeholder={loadingSymbols ? "Loading symbols..." : "Search symbol..."}
                searchPlaceholder="Type to search (e.g., NIFTY, COPPER)..."
                emptyText="No symbol found"
                disabled={loadingSymbols}
                data-testid="symbol-combobox"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contract_expiry" className="text-slate-300 text-sm font-medium">
                Contract Expiry <span className="text-red-400">*</span>
              </Label>
              <Select
                value={formData.contract_expiry}
                onValueChange={(value) => handleChange("contract_expiry", value)}
                disabled={loadingExpiries || contractExpiries.length === 0}
              >
                <SelectTrigger
                  id="contract_expiry"
                  data-testid="contract-expiry-select"
                  className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-white"
                >
                  <SelectValue placeholder={loadingExpiries ? "Loading..." : "Select expiry"} />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border-white/10 max-h-60">
                  {contractExpiries.map((contract) => (
                    <SelectItem key={contract.expiry} value={contract.expiry}>
                      {contract.expiry} {contract.lotsize && `(Lot: ${contract.lotsize})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.symbol && contractExpiries.length === 0 && !loadingExpiries && (
                <p className="text-xs text-yellow-500">Select a symbol to load expiries</p>
              )}
            </div>
          </div>

          {/* Direction and Entry Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="direction" className="text-slate-300 text-sm font-medium">
                Direction
              </Label>
              <Select
                value={formData.direction}
                onValueChange={(value) => handleChange("direction", value)}
              >
                <SelectTrigger
                  id="direction"
                  data-testid="direction-select"
                  className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-white"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border-white/10">
                  <SelectItem value="LONG" data-testid="direction-long">LONG</SelectItem>
                  <SelectItem value="SHORT" data-testid="direction-short">SHORT</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="entry_date" className="text-slate-300 text-sm font-medium">
                Entry Date
              </Label>
              <Input
                id="entry_date"
                data-testid="entry-date-input"
                type="date"
                value={formData.entry_date}
                onChange={(e) => handleChange("entry_date", e.target.value)}
                className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm font-mono text-sm text-white"
                required
              />
            </div>
          </div>

          {/* Entry Price and Lots */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="entry_price" className="text-slate-300 text-sm font-medium">
                Entry Price (₹)
              </Label>
              <Input
                id="entry_price"
                data-testid="entry-price-input"
                type="number"
                step="0.01"
                value={formData.entry_price}
                onChange={(e) => handleChange("entry_price", e.target.value)}
                className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm font-mono text-sm text-white"
                placeholder="0.00"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="num_lots" className="text-slate-300 text-sm font-medium">
                Number of Lots
              </Label>
              <Input
                id="num_lots"
                data-testid="num-lots-input"
                type="number"
                value={formData.num_lots}
                onChange={(e) => handleChange("num_lots", e.target.value)}
                className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm font-mono text-sm text-white"
                min="1"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lot_size" className="text-slate-300 text-sm font-medium">
                Lot Size
              </Label>
              <Input
                id="lot_size"
                data-testid="lot-size-input"
                type="number"
                value={formData.lot_size}
                onChange={(e) => handleChange("lot_size", e.target.value)}
                className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm font-mono text-sm text-white"
                min="1"
                placeholder="e.g., 50"
                required
              />
            </div>
          </div>

          {/* Entry Comments */}
          <div className="space-y-2">
            <Label htmlFor="entry_comments" className="text-slate-300 text-sm font-medium">
              Entry Comments (Optional)
            </Label>
            <Textarea
              id="entry_comments"
              data-testid="entry-comments-input"
              value={formData.entry_comments}
              onChange={(e) => handleChange("entry_comments", e.target.value)}
              className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-sm text-white min-h-[100px]"
              placeholder="Add notes about your trade strategy..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              data-testid="submit-trade-button"
              disabled={loading || !formData.account_id || !formData.strategy_id}
              className="bg-white text-black hover:bg-slate-200 rounded-sm font-medium px-8 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Adding..." : "Add Trade"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/trades")}
              className="bg-white/10 text-white hover:bg-white/20 rounded-sm font-medium px-8 py-3 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>

      {/* Add Account Dialog */}
      <Dialog open={showAccountDialog} onOpenChange={setShowAccountDialog}>
        <DialogContent className="bg-charcoal border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Account</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a trading account to track trades separately
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new_account_name" className="text-slate-300">
                Account Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="new_account_name"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                className="bg-black/20 border-white/10 text-white"
                placeholder="e.g., ASHOK-AO"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_account_broker" className="text-slate-300">
                Broker (Optional)
              </Label>
              <Input
                id="new_account_broker"
                value={newAccountBroker}
                onChange={(e) => setNewAccountBroker(e.target.value)}
                className="bg-black/20 border-white/10 text-white"
                placeholder="e.g., Angel One"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleAddAccount}
              className="bg-white text-black hover:bg-slate-200"
            >
              Add Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Strategy Dialog */}
      <Dialog open={showStrategyDialog} onOpenChange={setShowStrategyDialog}>
        <DialogContent className="bg-charcoal border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Strategy</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a trading strategy to categorize your trades
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new_strategy_name" className="text-slate-300">
                Strategy Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="new_strategy_name"
                value={newStrategyName}
                onChange={(e) => setNewStrategyName(e.target.value)}
                className="bg-black/20 border-white/10 text-white"
                placeholder="e.g., ATT-HYBRID"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_strategy_description" className="text-slate-300">
                Description (Optional)
              </Label>
              <Textarea
                id="new_strategy_description"
                value={newStrategyDescription}
                onChange={(e) => setNewStrategyDescription(e.target.value)}
                className="bg-black/20 border-white/10 text-white min-h-[80px]"
                placeholder="Brief description of the strategy..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleAddStrategy}
              className="bg-white text-black hover:bg-slate-200"
            >
              Add Strategy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddTrade;